import { useDocumentStore } from "@shared/components/providers/SupergrainProvider"
import type {
  CardStackAttributes,
  ParentCardStack,
  UnifiedCardStack,
} from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import copyLessonAction from "cc-frontend/lib/actions/lesson/LESSON_COPY"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type Notifier from "cc-frontend/services/notifier"
import type Session from "cc-frontend/services/session"
import { cloneDeep, isNil } from "lodash-es"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useEmberService } from "react-app/providers/ember-service-context"

interface LessonClipboard {
  /** Whether to offer Paste at all: the user can edit this plan and is signed in. */
  canPaste: boolean
  /** Copies the lesson to the user's clipboard (or prompts sign-in) and toasts. */
  copyLesson: () => void
  /** Pastes the user's clipboard into this slot, warning if nothing has been copied. */
  pasteLesson: () => void
  /**
   * Writes `attributes` into this slot, keeping a link back to where they came from. The pastes
   * that resolve their own source — a template, yesterday's lesson — share this write.
   */
  pasteInto: (
    attributes: CardStackAttributes,
    pastedAs: "lesson" | "template",
    parentCardStack: ParentCardStack,
  ) => void
}

/**
 * Copy and paste for one lesson slot — instructional or not. The clipboard lives on the user
 * document, so pasting reads it back through the store rather than from anything held here.
 */
export function useLessonClipboard({
  lesson,
  course,
  planbook,
  modelsNeededForFirstEdit,
}: {
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  course: Course
  planbook: Planbook
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
}): LessonClipboard {
  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const notifier = useEmberService<Notifier>("notifier")
  const session = useEmberService<Session>("session")
  const dispatch = useDispatch()
  const store = useDocumentStore()

  const canEditLesson = lesson.meta?.permissions?.includes("can-edit-plan") ?? false

  const pasteInto: LessonClipboard["pasteInto"] = (attributes, pastedAs, parentCardStack) =>
    lessonEditor.pasteCardStack(
      modelsNeededForFirstEdit,
      lesson.id,
      course,
      planbook,
      attributes,
      lesson.attributes,
      pastedAs,
      parentCardStack,
    )

  const canPaste = canEditLesson && !isNil(session.userId)

  const pasteLesson = () => {
    if (!canPaste) return
    void (async () => {
      // Read the handle's CURRENT value: the find promise memoizes its first
      // resolution, so it can hand back a stale user from before the copy wrote
      // the clipboard.
      const handle = store.find("user", session.userId)
      const user = handle.value ?? (await handle.promise)
      if (isNil(user?.attributes?.clipboard?.cardStackAttributes)) {
        notifier.warning("You need to copy a lesson first to paste it")
        return
      }
      const attributes: CardStackAttributes = cloneDeep(
        user.attributes.clipboard.cardStackAttributes,
      )
      pasteInto(attributes, "lesson", attributes.parentCardStack!)
    })()
  }

  const copyLesson = () => {
    if (session.isAuthenticated !== true) {
      session.mustSignInToProceed("Copy Lesson")
      return
    }
    if (session.userId !== null) {
      dispatch(
        copyLessonAction({
          lessonToCopy: lesson,
          cardStackId: lesson.id,
          currentUserId: session.userId,
          ownerIds: [planbook.relationships.owner.data.id],
          planbookId: planbook.id,
          courseId: course.id,
        }),
      )
    }
    notifier.success("", {
      title: "Lesson Copied!",
      message: "Click a lesson's paste button to paste.",
      duration: 1000,
    })
  }

  return { canPaste, copyLesson, pasteLesson, pasteInto }
}
